import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { StatusBadge } from '@/components/ui/status-badge';
import { paymentsApi } from '@/lib/pillarApi';
import type {
    MerchantPaymentRequestSearchRequest,
    PaymentRequestListItem,
    PaymentRequestStatus,
} from '@pillar-portal/api-types';
import { PaymentRequestStatus as PaymentRequestStatusValues } from '@pillar-portal/api-types';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { formatPaymentRequestSourceDocumentReference } from './source-document-reference';

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const PAYMENT_REQUEST_STATUS_FILTERS: readonly {
    value: string;
    label: string;
    statuses?: ReadonlyArray<PaymentRequestStatus>;
}[] = [
    { value: 'all', label: 'All statuses' },
    {
        value: 'open',
        label: 'Open',
        statuses: [
            PaymentRequestStatusValues.SENT,
            PaymentRequestStatusValues.VIEWED,
            PaymentRequestStatusValues.PARTIALLY_PAID,
        ],
    },
    { value: PaymentRequestStatusValues.PAID, label: 'Paid', statuses: [PaymentRequestStatusValues.PAID] },
    {
        value: PaymentRequestStatusValues.EXPIRED,
        label: 'Expired',
        statuses: [PaymentRequestStatusValues.EXPIRED],
    },
    {
        value: PaymentRequestStatusValues.CANCELED,
        label: 'Canceled',
        statuses: [PaymentRequestStatusValues.CANCELED],
    },
    {
        value: PaymentRequestStatusValues.REISSUED,
        label: 'Reissued',
        statuses: [PaymentRequestStatusValues.REISSUED],
    },
];

function formatAmount(amount: number | undefined, currencyCode: string): string {
    if (amount === undefined) {
        return '—';
    }
    if (currencyCode === 'USD') {
        return currencyFormatter.format(amount);
    }
    return `${amount.toFixed(2)} ${currencyCode}`;
}

function formatDate(value: string | undefined): string {
    if (value === undefined) {
        return '—';
    }
    return new Date(value).toLocaleString();
}

export function MerchantPaymentRequestsListContent() {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const activeStatusFilter =
        PAYMENT_REQUEST_STATUS_FILTERS.find((filter) => filter.value === statusFilter) ??
        PAYMENT_REQUEST_STATUS_FILTERS[0]!;
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['merchantPaymentRequests', `status=${statusFilter}`],
        [statusFilter]
    );

    return (
        <InfiniteScrollList<PaymentRequestListItem, number, ReadonlyArray<string>>
            pageTitle="Payment Requests"
            cardTitle="Payment Requests"
            queryPlaceholder="Search payment requests..."
            queryKeyPrefix={queryKeyPrefix}
            pageSize={50}
            fetchPage={async (params) => {
                const response = await paymentsApi.listMerchantPaymentRequests({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    statuses: activeStatusFilter.statuses
                        ? [...activeStatusFilter.statuses]
                        : undefined,
                    limit: params.limit,
                    cursor: params.cursor,
                } satisfies MerchantPaymentRequestSearchRequest);
                return { items: response.payment_requests, pagination: response.pagination };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.number().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected numeric cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading payment requests...',
                emptyLabel: 'No payment requests found.',
                endOfListLabel: 'End of payment requests.',
                genericErrorLabel: 'Failed to load payment requests.',
            }}
            controls={
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                        {PAYMENT_REQUEST_STATUS_FILTERS.map((filter) => (
                            <SelectItem key={filter.value} value={filter.value}>
                                {filter.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((paymentRequest) => {
                        const formattedSourceDocumentReference =
                            formatPaymentRequestSourceDocumentReference({
                                sourceDocumentReference:
                                    paymentRequest.source_document_reference,
                                sourceDocumentKey: paymentRequest.source_document_key,
                            });

                        return (
                            <button
                                key={paymentRequest.payment_request_uuid}
                                type="button"
                                className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                                onClick={() =>
                                    navigate(
                                        `/payment-requests/${paymentRequest.payment_request_uuid}`
                                    )
                                }
                            >
                                <div className="flex items-start justify-between gap-6">
                                    <div className="min-w-0 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold">
                                                {paymentRequest.title ??
                                                    formattedSourceDocumentReference ??
                                                    'Payment Request'}
                                            </h3>
                                            <StatusBadge status={paymentRequest.status} />
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            Reference:{' '}
                                            {formattedSourceDocumentReference ?? '—'}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            Recipient:{' '}
                                            {paymentRequest.recipient_email ??
                                                paymentRequest.recipient_phone ??
                                                '—'}
                                        </p>
                                    </div>
                                    <div className="space-y-1 text-right text-sm">
                                        <p className="font-semibold">
                                            {formatAmount(
                                                paymentRequest.original_amount,
                                                paymentRequest.currency_code
                                            )}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Created: {formatDate(paymentRequest.created_ts)}
                                        </p>
                                        <p className="text-muted-foreground">
                                            Expires: {formatDate(paymentRequest.expires_ts)}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        />
    );
}
